import React, { useState } from 'react';
import { Text, Link, Container, Stack } from 'basis';
import {
  byCard,
  getProductName,
  getConditionsOfUseTerm,
  getFinancialTableTerm,
  isLowRate,
  getProductInfo,
  getTerms,
} from 'utils';
import {
  OverviewSources,
  PRODUCT_CODE_AFS,
  PRODUCT_CODE_AU_GEM,
  PRODUCT_CODE_GO,
  PRODUCT_CODE_LOW_RATE,
  PRODUCT_CODE_TWENTY_EIGHT_DEGREES,
  config,
} from '_config';
import { ListItem as ChListItem, UnorderedList as ChUnorderedList } from '@chakra-ui/react';
import { isFeatureOn } from 'featureToggles';
import { VerifierModal } from '../landing-page-dj/VerifierModal';
import { DeclarationModal } from '../landing-page-dj/DeclarationModal';
import { PrivacyNoticeModal } from './PrivacyNoticeModal';

const productBenefits = {
  [PRODUCT_CODE_TWENTY_EIGHT_DEGREES]: [
    `the ability to make ${getTerms(
      'creditCardGeneralPurchases',
    )} in-store and online at businesses who accept Mastercard and perform other transactions.`,
    'pay no international transaction or currency conversion fees on purchases; and',
    'access to a Rewards program to earn redeemable amounts to purchase an e-Gift card on eligible transactions within a statement period.',
  ],
  [PRODUCT_CODE_LOW_RATE]: [
    `the ability to make ${getTerms(
      'creditCardGeneralPurchases',
    )} in-store and online at businesses who accept Mastercard and perform other transactions.`,
    'a low interest rate on general purchase transactions; and',
    'access to a Rewards program to earn redeemable amounts to purchase an e-Gift card on eligible transactions within a statement period.',
  ],
  [PRODUCT_CODE_AFS]: ['interest free payment plans, and', 'the ability to perform other transactions if required.'],
  default: [
    'interest free payment plans, and',
    `the ability to make ${getTerms('creditCardGeneralPurchases')} and / or perform other transactions if required.`,
  ],
};

export function Explain({ trackEventSender, formTealiumLocation }) {
  const benefitList = byCard(productBenefits);

  const [verifierModalOpen, setVerifierModalOpen] = useState(false);
  const [declarationModalOpen, setDeclarationModalOpen] = useState(false);
  const [privacyNoticeModalOpen, setPrivacyNoticeModalOpen] = useState(false);

  const openVerifierModal = () => {
    setVerifierModalOpen(true);
    trackEventSender({
      action: 'interaction',
      location: formTealiumLocation,
      label: "Verifier's terms of service",
    });
  };

  const closeVerifierModal = () => {
    setVerifierModalOpen(false);
  };

  const openDeclarationModal = () => {
    setDeclarationModalOpen(true);
    trackEventSender({
      action: 'interaction',
      location: formTealiumLocation,
      label: 'Declaration and Credit Application',
    });
  };

  const closeDeclarationModal = () => {
    setDeclarationModalOpen(false);
  };

  const openPrivacyNoticeModal = () => {
    setPrivacyNoticeModalOpen(true);
    trackEventSender({
      action: 'interaction',
      location: formTealiumLocation,
      label: 'Privacy Notice',
    });
  };

  const closePrivacyNoticeModal = () => {
    setPrivacyNoticeModalOpen(false);
  };

  const keyFactsSheetLink = byCard({
    [PRODUCT_CODE_GO]: OverviewSources.AU.go_key_facts_sheet,
    [PRODUCT_CODE_AFS]: OverviewSources.AU.afs_key_facts_sheet,
    [PRODUCT_CODE_AU_GEM]: OverviewSources.AU.key_facts_sheet,
    [PRODUCT_CODE_TWENTY_EIGHT_DEGREES]: OverviewSources.AU.twenty_eight_degrees_key_facts_sheet,
    [PRODUCT_CODE_LOW_RATE]: OverviewSources.AU.low_rate_key_facts_sheet,
  });

  const conditionsOfUseLink = byCard({
    [PRODUCT_CODE_GO]: OverviewSources.AU.go_conditions_of_use,
    [PRODUCT_CODE_AFS]: OverviewSources.AU.afs_conditions_of_use,
    [PRODUCT_CODE_AU_GEM]: OverviewSources.AU.gem_conditions_of_use,
    [PRODUCT_CODE_TWENTY_EIGHT_DEGREES]: OverviewSources.AU.twenty_eight_degrees_conditions_of_use,
    [PRODUCT_CODE_LOW_RATE]: OverviewSources.AU.low_rate_conditions_of_use,
  });

  const items = [
    {
      content: (
        <>
          <Container margin-sm="2 0 8 0" margin="6 0">
            <Text textStyle="heading4">Please make sure this product is right for you</Text>
          </Container>
          <Text>
            <strong>This credit card will give you:</strong>
          </Text>
          <UnorderedList>
            {benefitList.map((benefit, index) => (
              <ListItem key={`benefit-item-${index}`}>{benefit}</ListItem>
            ))}
          </UnorderedList>
        </>
      ),
    },
    {
      content: (
        <>
          <Text>
            <strong>By starting this application you agree:</strong>
          </Text>
          <UnorderedList>
            <ListItem>
              <Link
                newTab
                href={byCard({
                  [PRODUCT_CODE_AU_GEM]: OverviewSources.AU.latitude_gem_visa.url,
                  [PRODUCT_CODE_GO]: OverviewSources.AU.latitude_go_card.url,
                  [PRODUCT_CODE_AFS]: OverviewSources.AU.latitude_afs_card.url,
                  [PRODUCT_CODE_TWENTY_EIGHT_DEGREES]: OverviewSources.AU.latitude_28d_mastercard.url,
                  [PRODUCT_CODE_LOW_RATE]: OverviewSources.AU.latitude_low_rate_mastercard.url,
                })}
                onClick={() =>
                  trackEventSender({
                    action: 'external-link',
                    label: config.productName,
                    location: formTealiumLocation,
                  })
                }
              >
                {byCard({
                  [PRODUCT_CODE_AU_GEM]: OverviewSources.AU.latitude_gem_visa.label,
                  [PRODUCT_CODE_GO]: OverviewSources.AU.latitude_go_card.label,
                  [PRODUCT_CODE_AFS]: OverviewSources.AU.latitude_afs_card.label,
                  [PRODUCT_CODE_TWENTY_EIGHT_DEGREES]: getProductName(),
                  [PRODUCT_CODE_LOW_RATE]: getProductName(),
                  default: '',
                })}
              </Link>{' '}
              meets your key credit card requirements.
            </ListItem>
            <ListItem>
              you have read, understood and agree with the <Link onClick={openPrivacyNoticeModal}>Privacy Notice</Link>,{' '}
              <Link
                newTab
                href={keyFactsSheetLink.url}
                onClick={() =>
                  trackEventSender({
                    location: formTealiumLocation,
                    label: keyFactsSheetLink.label,
                  })
                }
              >
                {keyFactsSheetLink.label}
              </Link>
              ,{' '}
              <Link
                newTab
                href={conditionsOfUseLink.url}
                onClick={() =>
                  trackEventSender({
                    location: formTealiumLocation,
                    label: conditionsOfUseLink.label,
                  })
                }
              >
                {conditionsOfUseLink.label}
              </Link>
              ,{' '}
              {isLowRate() && (
                <>
                  <Link
                    newTab
                    href={getProductInfo()?.urls?.rewardsProgram}
                    onClick={() =>
                      trackEventSender({
                        location: formTealiumLocation,
                        label: getProductInfo()?.terms?.rewardsProgram,
                      })
                    }
                  >
                    {getProductInfo()?.terms?.rewardsProgram}
                  </Link>
                  ,{' '}
                </>
              )}
              <Link onClick={openDeclarationModal}>Declaration and Credit Application</Link>. These documents explain
              how your personal information is used, the product's features and terms under which this product operates.
            </ListItem>
            <ListItem>
              to let us complete a credit check and you acknowledge you have read the{' '}
              <Link
                newTab
                href="https://www.latitudefinancial.com.au/privacy/"
                onClick={() =>
                  trackEventSender({
                    location: formTealiumLocation,
                    label: 'Statement of Notifiable Matters',
                  })
                }
              >
                Statement of Notifiable Matters
              </Link>
              .
            </ListItem>
            <ListItem>
              to electronically receive your credit contract as set out in the {getFinancialTableTerm()} and{' '}
              {getConditionsOfUseTerm()}.
            </ListItem>
            <ListItem>
              to electronically verify your income using a secure process with Verifier (see{' '}
              <Link onClick={openVerifierModal}>Verifier's terms of service</Link>). If your income can't be verified
              this way, we will provide alternatives.
            </ListItem>
            <ListItem>
              that your personal information will be used for direct marketing. If you have previously opted out of
              direct marketing from us, you will be re-opted in. Refer to our{' '}
              <Link
                newTab
                href="https://www.latitudefinancial.com.au/privacy"
                onClick={() =>
                  trackEventSender({
                    location: formTealiumLocation,
                    label: 'Privacy Policy',
                  })
                }
              >
                Privacy Policy
              </Link>{' '}
              for further information.
            </ListItem>
          </UnorderedList>
        </>
      ),
    },
  ];

  return (
    <>
      <VerifierModal isOpen={verifierModalOpen} onClose={closeVerifierModal} />
      <DeclarationModal isOpen={declarationModalOpen} onClose={closeDeclarationModal} />
      <PrivacyNoticeModal isOpen={privacyNoticeModalOpen} onClose={closePrivacyNoticeModal} />
      <Stack gap="2">
        {items.map((item, index) => {
          return (
            <Container margin-md="0 0 6 0" key={`right-product-for-you-${index}`}>
              <Container margin-md="0 0 4 0">
                <Text textStyle="heading5" as="h4">
                  {item.content}
                </Text>
              </Container>
              {item.body}
            </Container>
          );
        })}
      </Stack>
    </>
  );
}

function ListItem({ children }) {
  return (
    <ChListItem>
      <Text margin={isFeatureOn('gemLandingAgreement') ? '0 0 3 0' : '0'}>{children}</Text>
    </ChListItem>
  );
}

export function UnorderedList({ children }) {
  return (
    <ChUnorderedList marginStart="-1.6em" marginInlineStart="-1.6em" fontSize="0.8em">
      {children}
    </ChUnorderedList>
  );
}
